// Осколки при потере бит в bodyVox: по одному debris на каждую вновь «убитую» ячейку.

import type { MeshInstance, Renderer } from "./renderer";
import {
  BODY_VOX_SX,
  BODY_VOX_SY,
  BODY_VOX_SZ,
  BODY_VOX_CUBE,
  BODY_VOX_OX,
  BODY_VOX_OY,
  BODY_VOX_OZ,
  BODY_VOX_GRID_BYTES,
} from "./bodyVox";
import { BODY_FLESH_DEBRIS_CUBE_SIZE, MEAT_MESH_DEBRIS_COLOR } from "./voxel";

const BODY_VOX_CELL_COUNT = BODY_VOX_SX * BODY_VOX_SY * BODY_VOX_SZ;
/** Защита от подвисания при массовом уроне / битом снапшоте. */
const MAX_DEBRIS_PER_DIFF = 200;
/** Сколы гладкой оболочки (shell-битмапа). */
const MAX_SHELL_DEBRIS_PER_DIFF = 100;
/** Сколы гладкой оболочки (shell): тот же красный тон, что мясо в mesh.frag. */
/** Кадры до разлёта: кубики видны на месте «вынутого» объёма, затем та же физика что у chunk debris. */
const PHYS_DEBRIS_ARM_DELAY_SEC = 2 / 60;

function cellIndexToIJK(cellIdx: number): [number, number, number] {
  const sxsy = BODY_VOX_SX * BODY_VOX_SY;
  const iz = (cellIdx / sxsy) | 0;
  const rem = cellIdx - iz * sxsy;
  const iy = (rem / BODY_VOX_SX) | 0;
  const ix = rem % BODY_VOX_SX;
  return [ix, iy, iz];
}

/** Сравнивает bodyVox с прошлым кадром и спавнит мелкие осколки в центрах новых дыр. */
export function syncBodyVoxCellDebris(renderer: Renderer, inst: MeshInstance): void {
  const cur = inst.bodyVoxAlive;
  if (!cur || cur.length !== BODY_VOX_GRID_BYTES || !inst.innerMesh) return;

  const carveCore = inst.innerBodyVoxShaderCarve !== false;
  const armAt = renderer.getTimeAccSec() + PHYS_DEBRIS_ARM_DELAY_SEC;
  const yaw = inst.yaw;
  const cs = Math.cos(yaw);
  const sn = Math.sin(yaw);

  const mcx = BODY_VOX_OX + BODY_VOX_SX * 0.5 * BODY_VOX_CUBE;
  const mcy = BODY_VOX_OY + BODY_VOX_SY * 0.5 * BODY_VOX_CUBE;
  const mcz = BODY_VOX_OZ + BODY_VOX_SZ * 0.5 * BODY_VOX_CUBE;

  if (carveCore) {
    if (!inst.bodyVoxPrevForDebris) {
      inst.bodyVoxPrevForDebris = new Uint8Array(BODY_VOX_GRID_BYTES);
      inst.bodyVoxPrevForDebris.set(cur);
    } else {
      const prev = inst.bodyVoxPrevForDebris;
      let spawned = 0;

      for (let byteIdx = 0; byteIdx < BODY_VOX_GRID_BYTES; byteIdx++) {
        if (spawned >= MAX_DEBRIS_PER_DIFF) break;
        const pb = prev[byteIdx];
        const cb = cur[byteIdx];
        if (pb === cb) continue;

        for (let bit = 0; bit < 8 && spawned < MAX_DEBRIS_PER_DIFF; bit++) {
          const mask = 1 << bit;
          if ((pb & mask) && !(cb & mask)) {
            const cellIdx = byteIdx * 8 + bit;
            if (cellIdx >= BODY_VOX_CELL_COUNT) continue;

            const [ix, iy, iz] = cellIndexToIJK(cellIdx);
            const lx = BODY_VOX_OX + (ix + 0.5) * BODY_VOX_CUBE;
            const ly = BODY_VOX_OY + (iy + 0.5) * BODY_VOX_CUBE;
            const lz = BODY_VOX_OZ + (iz + 0.5) * BODY_VOX_CUBE;

            let ox = lx - mcx;
            let oy = ly - mcy;
            let oz = lz - mcz;
            const len = Math.hypot(ox, oy, oz);
            if (len > 1e-6) {
              ox /= len;
              oy /= len;
              oz /= len;
            } else {
              ox = 0;
              oy = 0;
              oz = 1;
            }
            ox += (Math.random() - 0.5) * 0.35;
            oy += (Math.random() - 0.5) * 0.35;
            oz += (Math.random() - 0.5) * 0.25;
            const olen = Math.hypot(ox, oy, oz) || 1;
            ox /= olen;
            oy /= olen;
            oz /= olen;

            const speed = 2.5 + Math.random() * 3.0;
            const vx = (ox * cs - oy * sn) * speed;
            const vy = (ox * sn + oy * cs) * speed;
            const vz = oz * speed + 1.2 + Math.random() * 1.5;

            const wx = inst.x + (lx * cs - ly * sn);
            const wy = inst.y + (lx * sn + ly * cs);
            const wz = inst.z + lz;

            renderer.spawnChunkDebris(wx, wy, wz, vx, vy, vz, MEAT_MESH_DEBRIS_COLOR, BODY_FLESH_DEBRIS_CUBE_SIZE, {
              physicsArmAt: armAt,
            });
            spawned++;
            // Доп. мелкие кубы со сдвигом по объёму ячейки (псевдо-рандом «по толщине»).
            for (let e = 0; e < 2 && spawned < MAX_DEBRIS_PER_DIFF; e++) {
              const j = BODY_VOX_CUBE * 0.42;
              const dxL = (Math.random() - 0.5) * j;
              const dyL = (Math.random() - 0.5) * j;
              const dzL = (Math.random() - 0.5) * j;
              const wxx = inst.x + ((lx + dxL) * cs - (ly + dyL) * sn);
              const wyy = inst.y + ((lx + dxL) * sn + (ly + dyL) * cs);
              const wzz = inst.z + lz + dzL;
              renderer.spawnChunkDebris(
                wxx, wyy, wzz,
                vx * 0.92 + (Math.random() - 0.5) * 0.8,
                vy * 0.92 + (Math.random() - 0.5) * 0.8,
                vz * 0.92 + (Math.random() - 0.5) * 0.6,
                MEAT_MESH_DEBRIS_COLOR,
                BODY_FLESH_DEBRIS_CUBE_SIZE * 0.92,
                { physicsArmAt: armAt },
              );
              spawned++;
            }
            if (inst.realVoxelBody) {
              const small = BODY_FLESH_DEBRIS_CUBE_SIZE * 0.5;
              for (let k = 0; k < 2 && spawned < MAX_DEBRIS_PER_DIFF; k++) {
                const ang = Math.random() * Math.PI * 2;
                const spd = 0.75 + Math.random() * 2.2;
                const j = BODY_VOX_CUBE * 0.35;
                renderer.spawnChunkDebris(
                  wx + (Math.random() - 0.5) * j,
                  wy + (Math.random() - 0.5) * j,
                  wz + (Math.random() - 0.5) * j,
                  vx * 0.45 + Math.cos(ang) * spd,
                  vy * 0.45 + Math.sin(ang) * spd,
                  vz * 0.4 + Math.random() * 1.0 + 0.2,
                  MEAT_MESH_DEBRIS_COLOR,
                  small,
                  { physicsArmAt: armAt },
                );
                spawned++;
              }
            }
          }
        }
      }

      prev.set(cur);
    }
  } else {
    if (!inst.bodyVoxPrevForDebris) {
      inst.bodyVoxPrevForDebris = new Uint8Array(BODY_VOX_GRID_BYTES);
    }
    inst.bodyVoxPrevForDebris.set(cur);
  }

  const shellCur = inst.bodyVoxShell;
  if (!shellCur || shellCur.length !== BODY_VOX_GRID_BYTES) return;

  if (!inst.bodyVoxShellPrevForDebris) {
    inst.bodyVoxShellPrevForDebris = new Uint8Array(BODY_VOX_GRID_BYTES);
    inst.bodyVoxShellPrevForDebris.set(shellCur);
    return;
  }

  const sprev = inst.bodyVoxShellPrevForDebris;
  let sspawned = 0;
  for (let byteIdx = 0; byteIdx < BODY_VOX_GRID_BYTES; byteIdx++) {
    if (sspawned >= MAX_SHELL_DEBRIS_PER_DIFF) break;
    const pb = sprev[byteIdx];
    const cb = shellCur[byteIdx];
    if (pb === cb) continue;

    for (let bit = 0; bit < 8 && sspawned < MAX_SHELL_DEBRIS_PER_DIFF; bit++) {
      const mask = 1 << bit;
      if ((pb & mask) && !(cb & mask)) {
        const cellIdx = byteIdx * 8 + bit;
        if (cellIdx >= BODY_VOX_CELL_COUNT) continue;

        const [ix, iy, iz] = cellIndexToIJK(cellIdx);
        const lx = BODY_VOX_OX + (ix + 0.5) * BODY_VOX_CUBE;
        const ly = BODY_VOX_OY + (iy + 0.5) * BODY_VOX_CUBE;
        const lz = BODY_VOX_OZ + (iz + 0.5) * BODY_VOX_CUBE;

        let ox = lx - mcx;
        let oy = ly - mcy;
        let oz = lz - mcz;
        const len = Math.hypot(ox, oy, oz);
        if (len > 1e-6) {
          ox /= len;
          oy /= len;
          oz /= len;
        } else {
          ox = 0;
          oy = 0;
          oz = 1;
        }
        ox += (Math.random() - 0.5) * 0.45;
        oy += (Math.random() - 0.5) * 0.45;
        oz += (Math.random() - 0.5) * 0.35;
        const olen = Math.hypot(ox, oy, oz) || 1;
        ox /= olen;
        oy /= olen;
        oz /= olen;

        const speed = 3.2 + Math.random() * 4.5;
        const vx = (ox * cs - oy * sn) * speed;
        const vy = (ox * sn + oy * cs) * speed;
        const vz = oz * speed + 2.0 + Math.random() * 2.5;

        const wx = inst.x + (lx * cs - ly * sn);
        const wy = inst.y + (lx * sn + ly * cs);
        const wz = inst.z + lz;

        renderer.spawnChunkDebris(
          wx, wy, wz, vx, vy, vz,
          MEAT_MESH_DEBRIS_COLOR,
          BODY_FLESH_DEBRIS_CUBE_SIZE * 0.82,
          { physicsArmAt: armAt },
        );
        sspawned++;
      }
    }
  }

  sprev.set(shellCur);
}
